'use client';

import { useEffect } from 'react';

/**
 * Initialize Bootstrap components by loading the Bootstrap bundle
 * This avoids issues with server/client hydration mismatches
 */
export function useBootstrap() {
  useEffect(() => {
    const loadBootstrap = async () => {
      try {
        // Only import Bootstrap on the client side
        const { default: bootstrap } = await import('bootstrap/dist/js/bootstrap.bundle.min.js');
        
        // Wait a bit for Bootstrap to be fully loaded
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Initialize any Bootstrap components that need JavaScript
        // This will run after Bootstrap is loaded
        const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
        if (tooltipTriggerList.length > 0 && (window.bootstrap?.Tooltip || bootstrap?.Tooltip)) {
          const TooltipConstructor = window.bootstrap?.Tooltip || bootstrap?.Tooltip;
          const tooltipList = [...tooltipTriggerList].map(tooltipTriggerEl => {
            // Check if tooltip is already initialized
            const existingTooltip = TooltipConstructor.getInstance(tooltipTriggerEl);
            if (existingTooltip) {
              return existingTooltip;
            }
            return new TooltipConstructor(tooltipTriggerEl);
          });
        }
        
        const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
        if (popoverTriggerList.length > 0 && (window.bootstrap?.Popover || bootstrap?.Popover)) {
          const PopoverConstructor = window.bootstrap?.Popover || bootstrap?.Popover;
          const popoverList = [...popoverTriggerList].map(popoverTriggerEl => {
            // Check if popover is already initialized
            const existingPopover = PopoverConstructor.getInstance(popoverTriggerEl);
            if (existingPopover) {
              return existingPopover;
            }
            return new PopoverConstructor(popoverTriggerEl);
          });
        }
      } catch (error) {
        console.error('Failed to load Bootstrap:', error);
      }
    };

    loadBootstrap();
    
    // Clean up on unmount
    return () => {
      const tooltipTriggerList = document.querySelectorAll('[data-bs-toggle="tooltip"]');
      [...tooltipTriggerList].forEach(tooltipTriggerEl => {
        const tooltip = window.bootstrap?.Tooltip?.getInstance(tooltipTriggerEl);
        if (tooltip) tooltip.dispose();
      });
      
      const popoverTriggerList = document.querySelectorAll('[data-bs-toggle="popover"]');
      [...popoverTriggerList].forEach(popoverTriggerEl => {
        const popover = window.bootstrap?.Popover?.getInstance(popoverTriggerEl);
        if (popover) popover.dispose();
      });
    };
  }, []);
}

/**
 * Load Swiper components for carousels and sliders
 */
export function useSwiper() {
  useEffect(() => {
    const loadSwiper = async () => {
      try {
        // Import Swiper dynamically and assign to window
        const Swiper = await import('swiper');
        const { Navigation, Pagination, Autoplay } = await import('swiper/modules');
        
        // Configure Swiper to use modules
        window.Swiper = Swiper.default || Swiper;
        window.SwiperModules = { Navigation, Pagination, Autoplay };
        
        console.log('Swiper loaded successfully');
      } catch (error) {
        console.error('Failed to load Swiper:', error);
      }
    };

    if (typeof window !== 'undefined' && !window.Swiper) {
      loadSwiper();
    }
  }, []);
}

/**
 * Initialize theme-specific JavaScript functionality
 */
export function useThemeInit() {
  useEffect(() => {
    // Theme toggle functionality
    const themeMode = document.querySelector('.mode-switch');
    if (themeMode) {
      themeMode.addEventListener('click', () => {
        document.documentElement.classList.toggle('dark-mode');
      });
    }
    
    // Navbar behavior
    const handleScroll = () => {
      const navbar = document.querySelector('.navbar-sticky');
      if (navbar) {
        if (window.scrollY > 50) {
          navbar.classList.add('navbar-stuck');
        } else {
          navbar.classList.remove('navbar-stuck');
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initialize on load
    
    // Clean up
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (themeMode) {
        themeMode.removeEventListener('click', () => {
          document.documentElement.classList.toggle('dark-mode');
        });
      }
    };
  }, []);
} 